/**
 * The gold wax seal — the product's one ceremonial mark (design-spec/01 "Brand assets · The seal").
 *
 * Deliberately a plain, static, dependency-free component: it is both the reduced-motion face AND
 * the thing the animated ceremony wraps, so it must never import an animation library. See
 * `SealStamp.tsx` for the stamping wrapper and `lib/motion.ts` for why the split exists.
 *
 * Gold is reserved: it appears at the sign moment and nowhere earlier in the referee flow (the
 * review screen's dashed slot is the deliberate absence this fills).
 */
import { cn } from '@/lib/utils';

export type SealSize = 'sig' | 'done' | 'mini';

/**
 * Sizes from the signed-off mock, scaled to the real viewport rather than its 322px phone frame:
 *  - `sig`  — the seal that lands in the signature preview (mock `.seal--sig`, 56px)
 *  - `done` — the confirmation seal (mock `.seal--done`, 54px)
 *  - `mini` — the seal on the delivery card (mock `.seal--mini`, 32px)
 */
const SIZES: Record<SealSize, { box: string; text: string }> = {
  sig: { box: 'h-[62px] w-[62px]', text: 'text-[7px] tracking-[0.09em]' },
  done: { box: 'h-[64px] w-[64px]', text: 'text-[7.5px] tracking-[0.09em]' },
  mini: { box: 'h-[36px] w-[36px]', text: 'text-[5px] tracking-[0.06em]' },
};

interface SealProps {
  size?: SealSize;
  /** Seal face. The mock uses "By My Word" on the signature and "Verified" on the confirmation. */
  label?: string;
  className?: string;
}

/**
 * `aria-hidden` throughout: the seal is decoration for a fact the surrounding copy already states
 * in words ("You've given your word", "Verified by By My Word · Ref …"). Announcing "Verified" a
 * second time as an image would add noise, not meaning — and the design rule is that colour and
 * ornament never carry information alone.
 */
export function Seal({ size = 'done', label = 'Verified', className }: SealProps) {
  const { box, text } = SIZES[size];
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid flex-none place-items-center rounded-full border-2 text-center',
        // The exact seal recipe from design-spec/01: radial gold gradient, pale ring, inner
        // highlight, warm drop shadow. Arbitrary values because these are one-off brand constants,
        // not scale steps.
        //
        // The gradient comes from the `--grad-seal` TOKEN (packages/tokens/tokens.css), which is the
        // canonical definition of the recipe and the same one apps/landing paints the seal with.
        // It replaces an inline `radial-gradient(...)` that interpolated `theme(colors.gold-deep)` —
        // a path that does not exist, because the palette NESTS gold (`gold: { DEFAULT, deep, … }`)
        // so the lookup is dot notation. Tailwind's response to an invalid `theme()` inside an
        // arbitrary value is to drop the WHOLE utility with a `warn -`, and the build still exits 0:
        // the seal shipped with its border and shadow and no gold at all. jsdom applies no CSS, so
        // no test could catch it either. `bg-[image:var(--…)]` needs the `image:` hint — without it
        // Tailwind reads a bare `var()` as a colour and emits `background-color`.
        'border-[rgba(255,244,214,0.5)] bg-[image:var(--grad-seal)]',
        'shadow-[0_10px_20px_-8px_rgba(168,110,30,0.75),inset_0_1px_5px_rgba(255,255,255,0.4)]',
        box,
        className,
      )}
    >
      <span className={cn('font-display font-bold uppercase leading-[1.3] text-[#FFF3D8]', text)}>
        {label}
      </span>
    </span>
  );
}
